import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { Warning, ArrowBendUpLeft } from 'phosphor-react'
import { useImpersonation } from 'context/impersonation'
import { useNotification } from 'hooks/toast'

const Bar = styled.div`
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 20px;
    font-size: 13px;
    color: #451a03;
    background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
    border-bottom: 1px solid #d97706;
    .text {
        flex: 1;
    }
    button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        border-radius: 6px;
        border: none;
        font-size: 13px;
        cursor: pointer;
        color: #fff;
        background: rgba(0, 0, 0, 0.2);
        white-space: nowrap;
    }
    button:hover {
        background: rgba(0, 0, 0, 0.3);
    }
    button:disabled {
        opacity: 0.6;
        cursor: default;
    }
`

const pad = n => String(n).padStart(2, '0')

const formatCountdown = ms => {
    if (ms <= 0) return '00:00'
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${pad(minutes)}:${pad(seconds)}`
}

const minutesAgo = startedAtUtc => {
    if (!startedAtUtc) return null
    const started = new Date(startedAtUtc).getTime()
    if (Number.isNaN(started)) return null
    return Math.max(0, Math.floor((Date.now() - started) / 60000))
}

// Screen 3 of the handoff: fixed banner shown on every page during a session.
export const ImpersonationBanner = () => {
    const { session, isImpersonating, ending, endImpersonation, handleExpired } =
        useImpersonation()
    const { warningToast } = useNotification()
    const [remaining, setRemaining] = useState(null)
    const expiredRef = useRef(false)

    const hardCap = session?.hardCapExpiresAtUtc

    useEffect(() => {
        if (!isImpersonating || !hardCap) {
            setRemaining(null)
            return undefined
        }
        const expiresAt = new Date(hardCap).getTime()
        if (Number.isNaN(expiresAt)) {
            setRemaining(null)
            return undefined
        }

        const tick = () => {
            const diff = expiresAt - Date.now()
            setRemaining(diff)
            if (diff <= 0 && !expiredRef.current) {
                expiredRef.current = true
                warningToast('Impersonation session ended')
                handleExpired()
            }
        }

        tick()
        const timer = setInterval(tick, 1000)
        return () => clearInterval(timer)
        // warningToast is omitted on purpose: useNotification() returns a new
        // function each render; including it would reset the interval (and
        // re-run tick -> setState) on every render.
    }, [isImpersonating, hardCap, handleExpired])

    if (!isImpersonating || !session) return null

    const startedMinutes = minutesAgo(session.startedAtUtc)

    return (
        <Bar>
            <Warning size={18} weight="fill" />
            <div className="text">
                You are impersonating <strong>{session.targetName}</strong>
                {session.targetCompanyName && (
                    <>
                        {' '}
                        at <strong>{session.targetCompanyName}</strong>
                    </>
                )}
                {startedMinutes !== null && (
                    <> - started {startedMinutes} min ago</>
                )}
                {session.actorEmail && (
                    <> by <strong>{session.actorEmail}</strong></>
                )}
                .
                {remaining !== null && (
                    <>
                        {' '}
                        Session ends automatically in{' '}
                        <strong>{formatCountdown(remaining)}</strong>.
                    </>
                )}
            </div>
            <button
                type="button"
                onClick={endImpersonation}
                disabled={ending}
            >
                Return to admin <ArrowBendUpLeft size={16} weight="bold" />
            </button>
        </Bar>
    )
}
